import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './style.css';
import { ReactComponent as ArisLogo } from "../../../Assets/images/aris-logo.svg";
import { ReactComponent as IndividualIcon } from "../../../Assets/images/individual.svg";
import { ReactComponent as PartnerChatIcon } from "../../../Assets/images/partner_chat.svg";
import { ReactComponent as GroupChatIcon } from "../../../Assets/images/group_chat.svg";
import { ReactComponent as LockIcon } from "../../../Assets/icon/Lock.svg";
import ElementHeader from '../../../Components/ResizableComponent/ElementHeader';
import '../../../Components/ResizableComponent/style.css';
import { checkUserActivation, getSessions } from '../../../Services/coaching';
import DefaultLoader from '../../LoadingPage/DefaultLoader';
import FullScreenBackground from '../../../Components/BlueSvgBackground';
import { useHeader } from '../../../Providers/HeaderContextProvider';
import { getPreferredVideo } from "../../../Utils/media";
import ChatModal from '../../../Components/ChatModal/ChatModal';
import MobilePopUp from '../../../Components/MobilePopUp/MobilePopUp';
import GroupMemberSelectionModal from '../LiveSession/modals/session/GroupMemberSelectionModal';
import { Tooltip } from 'react-tooltip';
import RequestCoachingMinutes from './RequestCoachingMinutes';
import VoiceChatHistory from '../VoiceChatHistory';

const UserActivation = ({ relationId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [coupleCoaching, setCoupleCoaching] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [enableCoaching, setEnableCoaching] = useState(false);
  const [isBuyMinutesOpen, setIsBuyMinutesOpen] = useState(false);
  const { setHeaderContent } = useHeader();
  const [showDialoguesHistory, setShowDialoguesHistory] = useState(false);
  const historyRef = useRef(null);
  const isMobileView = screenWidth <= 600;
  
  // Helper: format seconds into a compact human-readable string
  const formatDuration = (totalSeconds) => {
    // Input is in seconds; display only whole minutes
    const seconds = Math.max(0, Math.floor(totalSeconds || 0));
    const minutesOnly = Math.floor(seconds / 60);
    return `${minutesOnly} minutes`;
  };
  
  // Fetch session data on component mount
  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const response = await getSessions();
        if (response && response.payload.minutes !== undefined) {
          // NOTE: backend now returns seconds instead of minutes
          const remainingSeconds = Math.floor(response.payload.minutes);
          setRemainingMinutes(remainingSeconds);
          setEnableCoaching(remainingSeconds > 0);
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
        // Default to enabled if there's an error
        setEnableCoaching(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionData();
  }, []);

  useEffect(() => {
    // Use the Elements-style header (hide global header)
    setHeaderContent(false);
  }, [setHeaderContent]);

  // Close history when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (historyRef.current && !historyRef.current.contains(e.target)) {
        setShowDialoguesHistory(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const VideoFile = getPreferredVideo(
    process.env.REACT_APP_SPHERE_LOADER || "https://www.aris.ai/ui-components/Sphere_Loader.webm",
    "https://www.aris.ai/ui-components/sphere-ios.mov"
  );

  const checkActivationAndProceed = async (action) => {
    setLoading(true);
    try {
      // Call the API to check user activation status
      const response = await checkUserActivation();
      
      if (response.activation === false) {
        // If user is not eligible, redirect to single person dialog for activation
        navigate('/coach/private?activation=true');
      } else {
        // If user is eligible, proceed with the selected action
        action();
      }
    } catch (error) { 
      console.error("Error checking user activation status:", error);
      setError("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualDialogue = () => {
    if (!enableCoaching) return;
    checkActivationAndProceed(() => navigate('/coach/private'));
  };

  const handlePartnerDialogue = () => {
    if (!enableCoaching) return;
    setCoupleCoaching(true);
    checkActivationAndProceed(() => setIsGroupModalOpen(true));
  };

  const handleGroupDialogue = () => {
    if (!enableCoaching) return;
    setCoupleCoaching(false);
    checkActivationAndProceed(() => setIsGroupModalOpen(true));
  };
  
  const handleBuyMinutes = () => {
    setIsBuyMinutesOpen(true);
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const handleHistoryClick = () => {
    // Navigate to coaching history page for a consistent history UX
    navigate('/coaching-history');
  };

  if (loading) {
    return (
      <FullScreenBackground>
        <div className="dialogues-loading">
          <DefaultLoader />
        </div>
      </FullScreenBackground>
    );
  }

  if (error) {
    return (
      <FullScreenBackground>
        <div className="dialogues-error">
          <p>{error}</p>
        </div>
      </FullScreenBackground>
    );
  }

  return (
    <FullScreenBackground>
      <ElementHeader
        onBackClick={handleBackClick}
        onHistoryClick={handleHistoryClick}
        isMobileView={isMobileView}
        periodicTableWidth={100}
        hideHistoryButton
      />
      <div className="dialogues-container">
        <div className="dialogues-content">
          <div className="dialogues-video-container">
            <video
              autoPlay
              loop
              muted
              playsInline
              webkit-playsinline="true"
              className="dialogues-gif"
              src={VideoFile}
            />
          </div>
          <div className="dialogues-welcome-text">
            <p>{remainingMinutes <= 0 ? 'Please add more minutes below to use Aris Dialogues' : 'Choose the Aris Dialogue that fits your moment.'}</p>
          </div>

          <div className="dialogues-options">
            <div className="dialogue-option" onClick={handleIndividualDialogue}>
              <IndividualIcon />
              <h3>For You</h3>
              <p className="dialogue-option-p">A private, voice-led session to reflect, reset and grow with Aris as your guide.</p>
            </div>

            <div className="dialogue-option" onClick={handlePartnerDialogue}>
              <PartnerChatIcon />
              <h3>For Two</h3>
              <p className="dialogue-option-p">Start a live session with an Aris member in your relationships.</p>
            </div>
          </div>

          <div className="dialogues-footer">
            <p className="minutes-remaining">
              <span>{formatDuration(remainingMinutes)}</span> remaining for your Aris Dialogues. <a
                href="#"
                className="dialogues-footer-link"
                onClick={(e) => { e.preventDefault(); handleBuyMinutes(); }}
              >
                Add more minutes
              </a>.
            </p>
            <div className="dialogues-footer-links" style={{ display: 'flex', gap: '16px', alignItems: 'center', position: 'relative' }}>
              <a
                href="#"
                className="dialogues-footer-link"
                onClick={(e) => { e.preventDefault(); setShowDialoguesHistory((p) => !p); }}
              >
                Aris Dialogues Session History
              </a>

              {showDialoguesHistory && (
                <div ref={historyRef} style={{ position: 'absolute', right: 0, bottom: '52px', zIndex: 20 }}>
                  <VoiceChatHistory
                    historyMenuClose={() => setShowDialoguesHistory(false)}
                    onHistorySessionClick={(item) => {
                      try {
                        const id = item?.coaching_id || item?.session_id || item?.sessionId || item?.id;
                        if (id) {
                          navigate(`/dialogues?sessionId=${encodeURIComponent(id)}`);
                        }
                      } finally {
                        setShowDialoguesHistory(false);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          
          <Tooltip id="disabled-tooltip" effect="solid" className="tooltip-custom" />
        </div>
      </div>

      {/* Request Coaching Minutes Modal */}
      <ChatModal
        isOpen={isBuyMinutesOpen}
        onClose={() => setIsBuyMinutesOpen(false)}
        width="462px"
        height="auto"
      >
        <RequestCoachingMinutes
          isOpen={isBuyMinutesOpen}
          onClose={() => setIsBuyMinutesOpen(false)}
          // Backward compatibility: modal expects minutes, convert seconds to minutes
          remainingMinutes={Math.floor(remainingMinutes / 60)}
          onSuccess={() => {
            // Refresh the session data when minutes are purchased
            const fetchSessionData = async () => {
              try {
                const response = await getSessions();
                if (response && response.payload.minutes !== undefined) {
                  const remainingSeconds = Math.floor(response.payload.minutes);
                  setRemainingMinutes(remainingSeconds);
                  setEnableCoaching(remainingSeconds > 0);
                }
              } catch (error) {
                console.error('Error fetching session data:', error);
              }
            };
            fetchSessionData();
          }}
        />
      </ChatModal>

      {/* Modal for Partner Dialogue */}
      {isGroupModalOpen && (
        <>
          {screenWidth > 480 ? (
            <ChatModal
              isOpen={isGroupModalOpen}
              onClose={() => setIsGroupModalOpen(false)}
              children={
                <GroupMemberSelectionModal
                  onClose={() => setIsGroupModalOpen(false)}
                  hideCloseIcon={false}
                  coupleCoaching={coupleCoaching}
                />
              }
              height={'fit-content'}
              width={'476px'}
            />
          ) : (
            <MobilePopUp
              isPopupVisible={isGroupModalOpen}
              onClose={() => setIsGroupModalOpen(false)}
              children={
                <GroupMemberSelectionModal
                  onClose={() => setIsGroupModalOpen(false)}
                  hideCloseIcon={true}
                  coupleCoaching={coupleCoaching}
                />
              }
              hideClose={false}
            />
          )}
        </>
      )}
    </FullScreenBackground>
  );
};

export default UserActivation;
